// English strings for the design-only screens (D-044): features seen in the Zalient admin on
// 2026-10-21 that Adyvance does not have yet. The screens show the layout; nothing is saved.
// Spread into `en` as `en.designs`, so keys read `t("designs.…")`. Copy rules: DESIGN_SYSTEM §9.6.
export const designsStrings = {
  previewTitle: "Design preview",
  previewBody:
    "This screen shows how the feature will work. It is not live yet, so nothing here is saved or shown on your store.",
  notLive: "Not live yet",
  add: "Add",
  edit: "Edit",
  integrationsLink: "Open Integrations",
  example: "Example",
  examplesNote: "Examples only. You write your own.",
  all: "All",
  status: "Status",

  featured: {
    lead: "Rows of products on your home page, like Best sellers or New arrivals. Drag to set the order.",
    action: "New section",
    listTitle: "Sections",
    emptyTitle: "No sections yet",
    emptyBody: "Add a section, then pick the products by hand or let a rule fill it.",
    rulesTitle: "Start from a rule",
    rules: {
      bestSellers: "Best sellers",
      bestSellersBody: "The most sold products in the last 30 days.",
      newArrivals: "New arrivals",
      newArrivalsBody: "Your newest products first.",
      onSale: "On sale",
      onSaleBody: "Products that have a discount right now.",
      handPicked: "Hand-picked",
      handPickedBody: "Only the products you choose, in your order.",
    },
  },

  sellingPoints: {
    lead: "Short reasons to buy from you, shown on your store, such as free delivery or easy returns.",
    action: "New selling point",
    emptyTitle: "No selling points yet",
    emptyBody: "Add two to four. Each one gets an icon, a short title and one line of detail.",
    previewTitle: "How they look on your store",
    samples: {
      delivery: "Free delivery",
      deliveryBody: "On orders over Rs 2,000",
      cod: "COD",
      codBody: "Pay when it arrives",
      returns: "Easy returns",
      returnsBody: "Within 7 days",
    },
  },

  installation: {
    lead: "Installation or fitting you offer with products, priced by city.",
    action: "New service",
    cols: {
      service: "Service",
      cities: "Cities",
      price: "Price",
      time: "Time",
      products: "Products",
    },
    emptyTitle: "No installation services yet",
    emptyBody:
      "Add a service, choose the cities where you offer it and its price in each, then turn it on for the products that need it. Customers pick it at checkout.",
  },

  newsletter: {
    lead: "Email addresses people leave in the newsletter form on your store.",
    action: "Export CSV",
    cols: { email: "Email", signedUp: "Signed up", source: "Form", consent: "Consent" },
    emptyTitle: "No subscribers yet",
    emptyBody:
      "Sign-ups from the newsletter form on your store appear here with the date and their consent. Only people who agreed get your emails.",
  },

  flashDeals: {
    lead: "Time-limited discounts on chosen products, shown with a countdown wherever the price appears.",
    action: "New flash deal",
    filters: { running: "Running", scheduled: "Scheduled", ended: "Ended" },
    cols: {
      deal: "Deal",
      products: "Products",
      discount: "Discount",
      starts: "Starts",
      ends: "Ends",
    },
    emptyTitle: "No flash deals yet",
    emptyBody:
      "Pick products, set the discount and the start and end time. Prices go back by themselves when the deal ends.",
  },

  links: {
    lead: "A link that opens checkout with the items already in the cart. Share it in chats and posts.",
    action: "New checkout link",
    cols: {
      link: "Link",
      items: "Items",
      discount: "Discount",
      opens: "Opens",
      orders: "Orders",
    },
    emptyTitle: "No checkout links yet",
    emptyBody: "Choose products and quantities, add a discount if you like, and copy the link.",
  },

  affiliates: {
    lead: "Partners who share your products get a tracked link and earn a commission on the orders they bring.",
    action: "Add affiliate",
    filters: { active: "Active", paused: "Paused" },
    stats: {
      orders: "Orders referred",
      sales: "Sales referred",
      owed: "Commission owed",
    },
    cols: {
      partner: "Partner",
      code: "Link code",
      products: "Products",
      commission: "Commission",
      orders: "Orders",
      earned: "Earned",
    },
    emptyTitle: "No affiliates yet",
    emptyBody:
      "Add a partner, choose which products they can promote and set the commission as a percentage or a fixed amount.",
  },

  blog: {
    lead: "Articles that bring visitors from search and social media.",
    action: "New post",
    filters: { published: "Published", drafts: "Drafts", scheduled: "Scheduled" },
    cols: { title: "Title", author: "Author", published: "Published", views: "Views" },
    emptyTitle: "No posts yet",
    emptyBody: "Write a post, add a cover image and choose when it goes live.",
  },

  branches: {
    lead: "Your shops and pickup points, shown on your store with the address, hours and a map.",
    action: "Add branch",
    cols: {
      branch: "Branch",
      address: "Address",
      phone: "Phone",
      hours: "Opening hours",
      map: "Map",
    },
    emptyTitle: "No branches yet",
    emptyBody: "Add each place customers can visit, with its opening hours and a map pin.",
  },

  markets: {
    lead: "Sell to other countries, each with its own currency, prices and delivery.",
    action: "Add market",
    cols: { market: "Market", currency: "Currency", prices: "Prices", delivery: "Delivery" },
    nepal: "Nepal",
    npr: "Nepali rupee (Rs)",
    base: "Your prices",
    home: "Your delivery rates",
    primary: "Primary",
    otherTitle: "Other countries",
    emptyBody:
      "Add a market for each country you ship to. Customers there see prices in their currency, and checkout uses that market's delivery rates.",
  },

  email: {
    lead: "How emails to your customers are sent and how they look.",
    smtp: "Send from your own address",
    smtpHelp: "Use your own mail server (SMTP) so order emails come from your domain.",
    templates: "Email templates",
    templatesHelp: "Change the order, delivery and log-in emails with your logo and colours.",
    templatesAction: "Edit templates",
    mailbox: "Business mailbox",
    mailboxHelp: "A mailbox on your domain to send and receive mail, with addresses like orders@.",
    mailboxAction: "Set up mailbox",
    serverTitle: "Mail server",
    host: "Server address",
    port: "Port",
    username: "Username",
    password: "Password",
    fromName: "Sender name",
    fromAddress: "Sender email",
  },

  help: {
    lead: "Guides for every part of your store, and a way to report a problem.",
    search: "Search the guides",
    topics: {
      start: "Getting started",
      startBody: "Set up your store and take your first order.",
      products: "Products",
      productsBody: "Add products, variants, photos and stock.",
      orders: "Orders",
      ordersBody: "Confirm, pack, send and track orders.",
      customers: "Customers",
      customersBody: "Customer records, segments and messages.",
      promotions: "Promotions",
      promotionsBody: "Coupons, automatic discounts and offers.",
      payments: "Payments",
      paymentsBody: "eSewa, Khalti, Fonepay, QR and COD.",
      delivery: "Delivery",
      deliveryBody: "Delivery charges and courier accounts.",
      settings: "Settings",
      settingsBody: "Store details, team, taxes and region.",
      faq: "Common questions",
      faqBody: "Short answers to what most sellers ask.",
    },
    moreTitle: "Need more help?",
    moreBody:
      "Your report includes the page you were on and your last steps, so you don't have to explain the setup.",
    report: "Report a problem",
    tickets: "Your reports",
  },

  checkout: {
    lead: "Everything that happens at checkout, in one place.",
    login: "Log in at checkout",
    loginHelp: "Customers sign in with email, phone or Google before they order.",
    mapPin: "Map pin for delivery",
    mapPinHelp:
      "Customers drop a pin for the delivery address. You can limit it to areas you serve.",
    feeTiers: "Delivery fee tiers",
    feeTiersHelp: "Cheaper or free delivery as the cart grows. Add as many steps as you need.",
    hideCity: "Hide the city picker",
    hideCityHelp: "For digital products or shops that don't need a delivery city.",
    hidePromo: "Hide the coupon box",
    hidePromoHelp: "Customers can't type a coupon code at checkout.",
    requireEmail: "Require email",
    requireEmailHelp: "Customers must give an email address to order.",
    minOrder: "Minimum order amount",
    minOrderHelp:
      "Checkout stays closed until the items total reaches this amount. Delivery and discounts don't count.",
    fields: "Extra fields",
    fieldsHelp:
      "Ask for more at checkout, such as company name, PAN or delivery notes. Answers are saved with the order.",
    fieldsAction: "Add field",
  },

  // Online store → Move your store (from the former App store, owner 2026-10-21).
  moveStore: {
    lead: "Bring an existing store to Adyvance.",
    title: "Move your store to Adyvance",
    body: "Bring products, categories and customers from another platform or a spreadsheet.",
    action: "Start import",
  },

  codeEditor: {
    lead: "Edit your theme's page layouts, components and styles directly. For developers.",
    explorer: "Files",
    newComponent: "New component",
    components: "Components",
    pages: "Pages",
    styles: "Styles",
    goToFile: "Go to file",
    addComponent: "Add component",
    ai: "Ask AI",
    preview: "Preview",
    visit: "Visit store",
    visual: "Visual editor",
    saveAll: "Save all",
    noFile: "No file open",
    noFileBody: "Pick a file on the left to start editing. Ctrl + S saves.",
    ready: "Ready",
    folders: {
      headers: "headers",
      products: "product-lists",
      footers: "footers",
      banners: "banners",
    },
  },

  pageEditor: {
    lead: "Arrange any page of your store by dragging sections, and see it on desktop, tablet and phone.",
    structure: "Structure",
    library: "Sections",
    page: "Page",
    pageHome: "Home page",
    desktop: "Desktop",
    tablet: "Tablet",
    phone: "Phone",
    undo: "Undo",
    redo: "Redo",
    addSection: "Add section",
    aiSection: "Make a section with AI",
    canvas: "Your page appears here",
    canvasBody: "Click a section to change its text, images and colours on the right.",
    properties: "Properties",
    noSelection: "Nothing selected",
    noSelectionBody: "Select a section on the page to edit it.",
    score: "Store score",
    scoreBody:
      "Checks your store against good selling practice (complete pages, trust signals, colours and fonts) and suggests fixes.",
    scoreAction: "Score my store",
    sections: { header: "Header", hero: "Banner", products: "Product list", footer: "Footer" },
  },

  themeLibrary: {
    lead: "Your current theme, its backups, and themes you can add, including paid themes and your own.",
    current: "Current theme",
    upToDate: "All parts are up to date",
    update: "Update parts",
    backups: "Backups",
    backupsBody:
      "Up to 5 saved copies plus automatic ones before each publish. Restore any of them.",
    backupsEmpty: "No backups yet",
    filters: { free: "Free", paid: "Paid", mine: "Made by you" },
    create: "New theme",
    emptyTitle: "The theme store isn't open yet",
    emptyBody:
      "Paid themes from designers, and themes you build in the code editor, will appear here with a preview and price.",
  },

  digitalProducts: {
    lead: "Sell things buyers get after payment instead of a parcel: a file, a licence key or a link.",
    enable: "Sell digital products",
    enableHelp: "Products marked digital skip delivery and shipping fees at checkout.",
    delivery: "How buyers get it",
    methods: {
      file: "File download",
      fileBody: "A PDF, music, design file or course pack, downloaded from the order page.",
      key: "Licence key",
      keyBody: "One key per order from a list you upload, such as software or game codes.",
      link: "Private link",
      linkBody: "A link to a class, video or shared folder, shown after payment.",
    },
    limits: "Limits and emails",
    downloads: "Download limit",
    downloadsHelp: "How many times each buyer can download the file.",
    expiry: "Link expiry",
    expiryHelp: "Days the download link keeps working after payment.",
    email: "Send by email",
    emailHelp: "Email the file, key or link to the buyer as soon as the payment is confirmed.",
  },

  traffic: {
    lead: "Who visits your store, from where and on which device, and where they leave.",
    stats: {
      visits: "Page visits",
      visitors: "Visitors",
      bounce: "Left after one page",
      session: "Time on store",
    },
    funnel: "Where buyers drop off",
    funnelSteps: {
      visited: "Visited",
      viewed: "Viewed a product",
      checkout: "Reached checkout",
      ordered: "Placed an order",
    },
    devices: "Devices",
    deviceNames: { desktop: "Desktop", mobile: "Phone", tablet: "Tablet" },
    sources: "Where visitors come from",
    noticed: "What we noticed",
    noticedBody:
      "Products with many views and no sales, with a suggested fix such as a clearer photo or visible delivery cost.",
    emptyBody: "Visitor numbers appear once store visit tracking is switched on.",
  },

  seo: {
    lead: "Help search engines and social apps show your store well.",
    titleRow: "Store title and description",
    titleHelp: "What search results show for your home page.",
    metaTitle: "Page title",
    metaDescription: "Description",
    tags: "Verification and meta tags",
    tagsHelp: "Paste tags from Google Search Console, Meta or Pinterest to verify your store.",
    tagsAction: "Add tag",
    sitemap: "Sitemap",
    sitemapHelp: "Made for you and kept up to date. Submit it to Google Search Console.",
    robots: "robots.txt",
    robotsHelp: "Choose which pages search engines may visit.",
    llms: "llms.txt for AI search",
    llmsHelp: "A short summary of your store that AI search tools can read.",
    feeds: "Product feeds",
    feedsHelp: "Google and Meta product feeds are set up in Integrations.",
  },
} as const;
